import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import CertificationBadge from '../../components/Certifications/CertificationBadge';
import { sendRequest } from '../../utils/dateHelpers';
import { buildCertificationProofUrl } from '../../utils/certificationLinks';

const EMBED_BACKEND_FALLBACKS =
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === 'localhost'
    ? [
        'http://127.0.0.1',
        'http://localhost',
        'http://127.0.0.1:80',
        'https://backend.gitdotme.com'
      ]
    : ['https://backend.gitdotme.com'];

const CertificationEmbedPage = () => {
  const { proofId = '' } = useParams();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const initialBadge = useMemo(() => {
    const displayName = searchParams.get('displayName');
    const companyName = searchParams.get('companyName');
    const shareCopy = searchParams.get('shareCopy');
    const badgeKey = searchParams.get('badgeKey');
    const status = searchParams.get('status') || 'active';

    if (!displayName && !badgeKey && !shareCopy) {
      return null;
    }

    return {
      display_name: displayName || 'Certification',
      company_name: companyName || 'GitMe Company',
      share_copy: shareCopy || '',
      badge_key: badgeKey || undefined,
      status
    };
  }, [searchParams]);
  const [badge, setBadge] = useState(initialBadge);
  const proofUrl = useMemo(
    () => buildCertificationProofUrl(proofId),
    [proofId]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchFromFallbackBackend = async () => {
      for (const baseUrl of EMBED_BACKEND_FALLBACKS) {
        try {
          const response = await fetch(
            `${baseUrl}/certifications/public?proofID=${encodeURIComponent(
              proofId
            )}&view=embed`
          );

          if (!response.ok) {
            continue;
          }

          const data = await response.json();

          if (data?.data) {
            return data.data;
          }
        } catch (error) {
          continue;
        }
      }

      return null;
    };

    const fetchProof = async () => {
      if (initialBadge) {
        if (isMounted) {
          setBadge(initialBadge);
        }
        return;
      }

      try {
        let response = await sendRequest(
          `/certifications/public?proofID=${encodeURIComponent(
            proofId
          )}&view=embed`
        );
        let nextBadge = response?.data || null;

        if (!nextBadge) {
          nextBadge = await fetchFromFallbackBackend();
        }

        if (isMounted) {
          setBadge(nextBadge);
        }
      } catch (error) {
        const nextBadge = await fetchFromFallbackBackend();

        if (isMounted) {
          setBadge(nextBadge);
        }
      }
    };

    if (proofId) {
      fetchProof();
    }

    return () => {
      isMounted = false;
    };
  }, [initialBadge, proofId]);

  useEffect(() => {
    document.documentElement.classList.add('certification-embed-document');
    document.body.classList.add('certification-embed-document');

    return () => {
      document.documentElement.classList.remove('certification-embed-document');
      document.body.classList.remove('certification-embed-document');
    };
  }, []);

  return (
    <div className="flex h-full w-full items-center justify-center overflow-hidden bg-transparent p-0">
      <a
        href={proofUrl}
        target="_top"
        className="flex h-full w-full cursor-pointer items-center justify-center"
        aria-label="Open certification proof"
      >
        <div
          className="flex w-full items-center justify-center"
          style={{ transform: 'scale(1)', transformOrigin: 'center center' }}
        >
          <CertificationBadge
            badge={
              badge || {
                display_name: 'Certification',
                company_name: 'GitMe Company',
                status: 'active'
              }
            }
            companyName={badge?.company_name || 'GitMe Company'}
            variant="embed"
          />
        </div>
      </a>
    </div>
  );
};

export default CertificationEmbedPage;
